import { LinkButton, LinkButtonProps } from '@/bloks/Button';
import { GlobalMenuPanelStoryblok } from '@/components';
import { useGlobalMenu } from '@/contexts/menu/GlobalMenuProvider';
import { useSearchV2 } from '@/contexts/search/AddSearchProviderV2';
import { ThemeProvider } from '@/contexts/theme';
import { getThemeInfo } from '@/themes';
import { cls } from '@/utils';
import { useEffect, useRef } from 'react';
import { GlobalMenuCard } from '../GlobalMenuCard/GlobalMenuCard';
import { LinkGroup } from '../LinkGroup/LinkGroup';
import styles from './MenuPanelDesktop.module.scss';

interface Props {
	panel?: GlobalMenuPanelStoryblok | null;
	footerLinks?: LinkButtonProps[];
	callbackHeight: (height: number) => void;
	active: boolean;
	className?: string;
}

export const MenuPanelDesktop: React.FC<Props> = ({ panel, footerLinks, callbackHeight, active, className }) => {
	const { isMenuOpen } = useGlobalMenu();
	const { isSearchOpen } = useSearchV2();
	const theme = 'menuContent';
	const themeInfo = getThemeInfo(theme);
	const tabIndex = isMenuOpen ? 0 : -1;
	const innerRef = useRef<HTMLDivElement | null>(null);
	const footerRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		function handleResize() {
			if (innerRef.current && footerRef.current && active) {
				const height = innerRef.current.clientHeight + footerRef.current.clientHeight;
				callbackHeight?.(height);
			}
		}
		handleResize();

		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, [callbackHeight, active, isSearchOpen]);

	if (!panel) {
		return null;
	}

	const { mainLinkGroup, linkGroup, card } = panel || {};

	return (
		<ThemeProvider theme={theme}>
			<div
				className={cls(
					styles.menuContent,
					themeInfo.styles.bgColor,
					isMenuOpen && active && styles.isMenuOpen,
					active && styles.active,
					className,
				)}
			>
				<div className={styles.inner} ref={innerRef}>
					<div className={styles.linkGroupsContainer}>
						{mainLinkGroup?.map((group) => (
							<LinkGroup key={group._uid} {...LinkGroup.blokProps({ blok: group })} main />
						))}

						<div className={styles.linkGroups}>
							{linkGroup?.map((group) => <LinkGroup key={group._uid} {...LinkGroup.blokProps({ blok: group })} />)}
						</div>
					</div>
					{card?.[0] && <GlobalMenuCard {...GlobalMenuCard.blokProps({ blok: card[0] })} />}
				</div>
				<footer className={styles.footer} ref={footerRef}>
					{footerLinks?.map((link) => <LinkButton key={link._uid} {...link} variant="link" tabIndex={tabIndex} />)}
				</footer>
			</div>
		</ThemeProvider>
	);
};
